import type { TCData } from "./types";

type TCFCommand = "getTCData" | "addEventListener" | "removeEventListener";

export type TCFAPI = (
  _command: TCFCommand,
  _version: 2,
  _callback: (..._args: any[]) => void,
  _parameter?: any
) => void;

declare global {
  interface Navigator {
    readonly globalPrivacyControl?: boolean;
  }

  interface Window {
    __tcfapi?: TCFAPI;
  }
}

const FINAL_TCF_EVENT_STATUSES = new Set(["tcloaded", "useractioncomplete"]);

export function isGPCEnabled(): boolean {
  return (
    typeof navigator !== "undefined" && navigator.globalPrivacyControl === true
  );
}

/**
 * Returns undefined while the CMP has not provided an authoritative decision.
 * The caller currently treats that state as allowed (fail open).
 */
export function getTCFConsentDecision(
  tcData: TCData | undefined
): boolean | undefined {
  if (tcData?.gdprApplies === false) return true;
  if (tcData?.gdprApplies !== true) return undefined;
  if (!FINAL_TCF_EVENT_STATUSES.has(tcData.eventStatus)) return undefined;

  const consents = tcData.purpose?.consents;
  if (consents == null) return undefined;

  const canStoreOrAccessDeviceInformation = consents["1"] === true;
  const hasMeasurementOrImprovementConsent =
    consents["7"] === true || consents["10"] === true;

  return (
    canStoreOrAccessDeviceInformation && hasMeasurementOrImprovementConsent
  );
}

export function subscribeToConsent(
  callback: (_consentGiven: boolean) => void
): () => void {
  let lastDecision: boolean | undefined;
  let failOpenTimeout: number | undefined;
  const emit = (consentGiven: boolean) => {
    if (lastDecision === consentGiven) return;
    lastDecision = consentGiven;
    callback(consentGiven);
  };

  if (isGPCEnabled()) {
    emit(false);
    return () => {};
  }

  const tcfapi = window.__tcfapi;
  if (typeof tcfapi !== "function") {
    emit(true);
    return () => {};
  }

  let listenerId: number | undefined;
  try {
    failOpenTimeout = window.setTimeout(() => emit(true), 1000);
    tcfapi("addEventListener", 2, (tcData: TCData, success: boolean) => {
      window.clearTimeout(failOpenTimeout);
      failOpenTimeout = undefined;
      if (isGPCEnabled()) {
        emit(false);
        return;
      }

      if (!success) {
        emit(true);
        return;
      }

      listenerId = tcData?.listenerId ?? listenerId;
      emit(getTCFConsentDecision(tcData) ?? true);
    });
  } catch {
    window.clearTimeout(failOpenTimeout);
    failOpenTimeout = undefined;
    emit(true);
  }

  return () => {
    window.clearTimeout(failOpenTimeout);
    if (listenerId != null) {
      try {
        tcfapi("removeEventListener", 2, () => {}, listenerId);
      } catch {
        // The CMP may have been removed before page teardown.
      }
    }
  };
}
